/**
 * LeaveRequestForm — Story 106-2 (E-106 HR UX Max)
 *
 * Fixek a korábbi verzióhoz képest:
 *  1. leaveTypeId mostantól UUID (useLeaveTypes) — a backend Zod .uuid() validáció
 *     miatt a submit 400 Bad Request-et dobott, ha code-ot küldtünk ("ANNUAL").
 *  2. Chip-picker dátum preset (E-105 Story 105-5 pattern reuse) — mobile-first.
 *  3. Quota strip: kiválasztott típushoz maradék nap progress-bar.
 *  4. Working-days preview: submit előtt megmutatja hány munkanap (H-P).
 *  5. Mobile stack layout + sticky submit button.
 *  6. Overlap error UX (Story 106-1a wiring).
 */

import { useApiError } from '@/hooks/use-api-error';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@kgc/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { requestLeave, type RequestLeaveInput } from '../api/hr-api';
import { leaveRequestsQueryKey } from '../hooks/useLeaveRequests';
import { useLeaveTypes } from '../hooks/useLeaveTypes';
import { useMyLeaveBalance } from '../hooks/useMyLeaveBalance';

// E-105 Story 105-5 pattern: chip preset ID-k
type DateChipId = 'tomorrow' | 'nextMonday' | 'oneWeek' | 'custom';

function toIso(d: Date): string {
  return d.toISOString().split('T')[0] ?? '';
}

function addDays(d: Date, days: number): Date {
  const result = new Date(d);
  result.setDate(result.getDate() + days);
  return result;
}

/** Chip-preset → (startDate, endDate) ISO `YYYY-MM-DD` vagy null (custom). */
function computeChipDates(chip: DateChipId): { start: string; end: string } | null {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  switch (chip) {
    case 'tomorrow': {
      const tomorrow = addDays(today, 1);
      return { start: toIso(tomorrow), end: toIso(tomorrow) };
    }
    case 'nextMonday': {
      // Következő hétfő (ha ma hétfő → jövő hétfő)
      const dayOfWeek = today.getDay(); // 0=Vas, 1=Hé, ..., 6=Szo
      const daysUntilMonday = dayOfWeek === 1 ? 7 : (8 - dayOfWeek) % 7 || 7;
      const monday = addDays(today, daysUntilMonday);
      return { start: toIso(monday), end: toIso(monday) };
    }
    case 'oneWeek': {
      // Következő H-tól 5 munkanap (péntek)
      const dayOfWeek = today.getDay();
      const daysUntilMonday = dayOfWeek === 1 ? 7 : (8 - dayOfWeek) % 7 || 7;
      const monday = addDays(today, daysUntilMonday);
      const friday = addDays(monday, 4);
      return { start: toIso(monday), end: toIso(friday) };
    }
    case 'custom':
      return null;
  }
}

/**
 * Kliens-oldali munkanap-előnézet (H-P = munkanap, Sz-V = hétvége).
 * A backend dominál a végleges számításban (Mt. 145 ünnep is kalkulált ott).
 */
function countWorkingDays(startIso: string, endIso: string): number {
  if (!startIso || !endIso) return 0;
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) return 0;
  let count = 0;
  const current = new Date(start);
  while (current <= end) {
    const day = current.getDay();
    if (day !== 0 && day !== 6) count++;
    current.setDate(current.getDate() + 1);
  }
  return count;
}

const CHIP_IDS: DateChipId[] = ['tomorrow', 'nextMonday', 'oneWeek', 'custom'];

interface LeaveRequestFormProps {
  onSuccess?: () => void;
}

export function LeaveRequestForm({ onSuccess }: LeaveRequestFormProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();
  const queryClient = useQueryClient();

  const { data: leaveTypes = [], isLoading: typesLoading } = useLeaveTypes();
  const currentYear = new Date().getFullYear();
  const { data: myBalanceResult } = useMyLeaveBalance(currentYear);
  const balances = myBalanceResult?.balances ?? [];

  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [selectedChip, setSelectedChip] = useState<DateChipId>('custom');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  const selectedType = useMemo(
    () => leaveTypes.find(lt => lt.id === leaveTypeId),
    [leaveTypes, leaveTypeId]
  );
  const selectedBalance = useMemo(
    () => balances.find(b => b.leaveTypeId === leaveTypeId),
    [balances, leaveTypeId]
  );

  // Quota strip csak fizetett szabadság-típusokra (pl. ANNUAL). UNPAID elrejtve.
  const showQuota = Boolean(
    selectedType && selectedBalance && (selectedType.paidPercentage ?? 0) > 0
  );

  const workingDays = useMemo(() => countWorkingDays(startDate, endDate), [startDate, endDate]);

  const handleChipSelect = (chip: DateChipId) => {
    setSelectedChip(chip);
    const preset = computeChipDates(chip);
    if (preset !== null) {
      setStartDate(preset.start);
      setEndDate(preset.end);
    } else {
      // custom → nem módosítjuk a date input-ot, a user gépel
      setStartDate('');
      setEndDate('');
    }
  };

  const mutation = useMutation({
    mutationFn: (data: RequestLeaveInput) => requestLeave(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: leaveRequestsQueryKey });
      toast.success(t('hr.leave.requestSubmitted'));
      setLeaveTypeId('');
      setStartDate('');
      setEndDate('');
      setReason('');
      setSelectedChip('custom');
      onSuccess?.();
    },
    onError: (err: Error) => {
      // 49Y hibakód-batch: a strukturált ApiError.code (LEAVE_OVERLAP / LEAVE_INSUFFICIENT_BALANCE
      // / EMPLOYEE_PROFILE_NOT_FOUND — #895 BE) → code-alapú lokalizált toast a getErrorMessage-en
      // át; fallback a régi requestError-üzenetre. Kiváltja a törékeny err.message string-matchet.
      toast.error(getErrorMessage(err, t('hr.leave.requestError')));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leaveTypeId || !startDate || !endDate) {
      toast.error(t('hr.leave.missingFields'));
      return;
    }
    if (new Date(startDate) > new Date(endDate)) {
      toast.error(t('hr.leave.invalidDateRange'));
      return;
    }
    if (showQuota && selectedBalance && workingDays > selectedBalance.remaining) {
      toast.error(t('hr.leave.insufficientBalance'));
      return;
    }
    mutation.mutate({
      leaveTypeId, // 106-2: UUID, NEM kód
      startDate,
      endDate,
      reason: reason || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Szabadság típus */}
      <div className="space-y-1">
        <label className="text-sm font-medium">{t('hr.leave.type')}</label>
        <Select value={leaveTypeId} onValueChange={setLeaveTypeId} disabled={typesLoading}>
          <SelectTrigger>
            <SelectValue placeholder={t('hr.leave.selectType')} />
          </SelectTrigger>
          <SelectContent>
            {leaveTypes.map(lt => (
              <SelectItem key={lt.id} value={lt.id}>
                {lt.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Quota strip — csak fizetett típusoknál */}
      {showQuota && selectedBalance && (
        <div className="rounded-lg border bg-muted/30 p-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="font-medium">{selectedType?.name ?? ''}</span>
            <span className="text-muted-foreground">
              {t('hr.leave.quotaLabel', {
                remaining: selectedBalance.remaining,
                entitled: selectedBalance.entitled,
              })}
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
            <div
              className="h-full bg-primary transition-all"
              style={{
                width: `${
                  selectedBalance.entitled > 0
                    ? Math.max(
                        0,
                        Math.min(100, (selectedBalance.remaining / selectedBalance.entitled) * 100)
                      )
                    : 0
                }%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Chip dátum preset (E-105 pattern reuse) */}
      <div className="space-y-2">
        <label className="text-sm font-medium">{t('hr.leave.datePreset')}</label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {CHIP_IDS.map(chip => (
            <Button
              key={chip}
              type="button"
              variant={selectedChip === chip ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleChipSelect(chip)}
            >
              {t(`hr.leave.chip.${chip}`)}
            </Button>
          ))}
        </div>
      </div>

      {/* Egyéni dátum mezők — csak ha 'custom' van kiválasztva, VAGY alapértelmezettként */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('hr.leave.startDate')}</label>
          <Input
            type="date"
            value={startDate}
            onChange={e => {
              setStartDate(e.target.value);
              setSelectedChip('custom');
            }}
            required
          />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">{t('hr.leave.endDate')}</label>
          <Input
            type="date"
            value={endDate}
            min={startDate}
            onChange={e => {
              setEndDate(e.target.value);
              setSelectedChip('custom');
            }}
            required
          />
        </div>
      </div>

      {/* Working-days preview */}
      {workingDays > 0 && (
        <p className="text-sm text-muted-foreground" aria-live="polite">
          {t('hr.leave.workingDaysPreview', { count: workingDays })}
        </p>
      )}

      {/* Indoklás */}
      <div className="space-y-1">
        <label className="text-sm font-medium">{t('hr.leave.reason')}</label>
        <Textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t('hr.leave.reasonPlaceholder')}
          rows={3}
        />
      </div>

      <Button
        type="submit"
        disabled={mutation.isPending || typesLoading}
        className="w-full sm:w-auto"
      >
        {mutation.isPending ? t('common.saving') : t('hr.leave.request')}
      </Button>
    </form>
  );
}
